import { FileText, Clock, Users, CheckCircle2, Loader2, Pause, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getCampaignRealStatus } from '@common-reducers/CampaignThunks';
import { setActiveBulkView, setCampaignIdForLogsView, openBulkDashboard, setActiveTab } from '@common-reducers/CampaignReducer';
import { taskActions } from '@common-reducers/TasksReducer';
import { campaignSelectors } from "@common-reducers/DBServiceThunks";
import { selectCampaignNumberOfContactsByCampaignId } from '@common-reducers/CampaignSelectors';
import { useAppSelector, useAppDispatch } from '@common-reducers/hooks/store.hook';
import { memo, useMemo, useCallback } from 'react';
import CampaignActionsMenu from './campaign-actions-menu';
import InProgressCampaignIndicator from './in-progress-campaign-indicator';
import TemplateRowDetails from './template-row-details';
import { RealCampaignStatus } from './campaign-dialog-row';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

type Props = {
    campaignId: string;
};

function CampaignCard({ campaignId }: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const campaign = useAppSelector(state => campaignSelectors.selectById(state, campaignId));
    const numberOfContacts = useAppSelector(state => selectCampaignNumberOfContactsByCampaignId(state, campaign?._id));
    const realStatus = getCampaignRealStatus(campaign?.campaignStatus, !!campaign?.contactCount, (campaign as any)?.updatedAt ?? campaign?.schedule?.dueDate) as RealCampaignStatus;

    const handleOpenLogs = useCallback(() => {
        if (campaignId) {
            dispatch(taskActions.setIsTasksPanelOpen(false));
            dispatch(setCampaignIdForLogsView(campaignId));
            dispatch(setActiveTab("manageCampaigns"));
            dispatch(setActiveBulkView("campaignLogsView"));
            dispatch(openBulkDashboard());
        }
    }, [campaignId, dispatch]);

    const formatISOCampaignCreationTime = (isoString: string) => {
        if (!isoString) return t('campaign.startTimeUnavailable');
        const date = new Date(isoString);
        const formattedDate = date.toLocaleDateString('en-GB');
        const formattedTime = date.toLocaleTimeString('en-GB', { hour12: false });
        return t('campaign.started', { date: formattedDate, time: formattedTime });
    };

    const totalNumberOfContacts = useMemo(() => {
        if (campaign?.contactCount) {
            return campaign.contactCount;
        } else if (campaign?.list && campaign?.list?.contactIdList) {
            return campaign.list.contactIdList.length;
        }
        return 0;
    }, [campaign]);

    const hasFiles = useMemo(() => {
        return !!(campaign?.template?.asset || campaign?.template?.sendLinkPreview || campaign?.template?.linkPreview?.url);
    }, [campaign?.template]);

    const getStatusBadge = () => {
        const baseClasses = "cursor-pointer hover:opacity-80 transition-opacity";
        switch (realStatus) {
            case 'Completed':
                return (
                    <div onClick={handleOpenLogs} className={cn("flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg font-medium", baseClasses)}>
                        <CheckCircle2 className="w-4 h-4" />
                        <span>{t('campaignStatus.completed')}</span>
                    </div>
                );
            case 'Running':
                return (
                    <div onClick={handleOpenLogs} className={cn("flex items-center gap-2 px-4 py-2 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 rounded-lg font-medium", baseClasses)}>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>{t('campaignStatus.running')}</span>
                    </div>
                );
            case 'Paused':
                return (
                    <div onClick={handleOpenLogs} className={cn("flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg font-medium", baseClasses)}>
                        <Pause className="w-4 h-4" />
                        <span>{t('campaignStatus.paused')}</span>
                    </div>
                );
            case 'Aborted':
                return (
                    <div onClick={handleOpenLogs} className={cn("flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg font-medium", baseClasses)}>
                        <XCircle className="w-4 h-4" />
                        <span>{t('campaignStatus.aborted')}</span>
                    </div>
                );
            case 'Pending':
            default:
                return (
                    <div onClick={handleOpenLogs} className={cn("flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-[rgb(57,58,74)] text-slate-700 dark:text-slate-300 rounded-lg font-medium", baseClasses)}>
                        <Clock className="w-4 h-4" />
                        <span>{t('campaignStatus.pending')}</span>
                    </div>
                );
        }
    };

    if (!campaign) return null;

    return (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                    {/* Campaign Name and Type Badge */}
                    <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 truncate">
                            {campaign.template?.name || campaign.name || t('campaign.unnamed')}
                        </h2>
                        {hasFiles && (
                            <span className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 rounded-full text-sm font-medium flex-shrink-0">
                                <FileText className="w-3.5 h-3.5" />
                                {t('campaign.file')}
                            </span>
                        )}
                    </div>

                    {/* Template Description/Message */}
                    <div className="mb-4">
                        {campaign.template?.message && campaign.template.message !== '' && !campaign.template.isPoll && (
                            <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-2">
                                {campaign.template.message}
                            </p>
                        )}
                        {campaign.template?.isPoll && (
                            <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-2">
                                {t('campaign.poll', { question: campaign.template.poll?.question })}
                            </p>
                        )}
                        {!campaign.template?.message && !campaign.template?.isPoll && campaign.template?.asset && (
                            <TemplateRowDetails template={campaign.template ?? {}} />
                        )}
                    </div>

                    {/* Campaign Details (Date and Contacts) */}
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 flex-shrink-0" />
                            <span>{formatISOCampaignCreationTime(campaign?.schedule?.dueDate)}</span>
                        </div>
                        {totalNumberOfContacts > 0 && (
                            <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 flex-shrink-0" />
                                <span className="font-medium text-emerald-600 dark:text-emerald-400">
                                    {t('campaign.contacts', { count: totalNumberOfContacts })}
                                </span>
                            </div>
                        )}
                    </div>

                </div>

                {/* Status Badge/Progress Indicator and Actions Menu */}
                <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                    {campaign?.contactCount && (realStatus === 'Running' || realStatus === 'Paused') ? (
                        <InProgressCampaignIndicator
                            campaignId={campaign._id}
                        />
                    ) : (
                        getStatusBadge()
                    )}
                    <CampaignActionsMenu campaignId={campaign._id} />
                </div>
            </div>
        </div>
    );
}

export default memo(CampaignCard);

